import { Tooltip as MuiTooltip } from '@mui/material';

/**
 * Tooltip — thin MUI wrapper. Default placement is 'top' to match the
 * v3 design system. Override via `placement` prop if needed.
 *
 * Default delays follow the sidebar-nav recipe (the reference tooltip
 * behavior in this app): a short 100 ms enter delay so hover feels
 * immediate, and a 50 ms enterNextDelay so moving between adjacent
 * tooltipped controls doesn't flicker. Never fall back to the native
 * `title` attribute for hints — its browser-controlled ~1 s delay
 * reads as "no tooltip at all".
 *
 * Drawn as the same card as the "i" hints (HintPopover's HintCard): a
 * surface card with a border and a soft shadow, secondary text. MUI's
 * default dark slab was a second hint style beside it, and the dashboard
 * should have one (customer review 2026-10-28).
 */
const HINT_CARD_SX = {
  bgcolor: 'var(--qhs-surface)',
  color: 'var(--qhs-text-2)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 'var(--qhs-r-xl)',
  boxShadow: 'var(--qhs-sh-3)',
  fontFamily: 'var(--qhs-font-body)',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.45,
  padding: '10px 14px',
  maxWidth: 360,
};

const ARROW_SX = {
  color: 'var(--qhs-surface)',
  '&::before': { border: '1px solid var(--qhs-border)' },
};

// A caller's own `sx` still wins: it is applied after the card's.
const withCardSx = (slot, base) => ({
  ...slot,
  sx: [base, ...(Array.isArray(slot?.sx) ? slot.sx : slot?.sx ? [slot.sx] : [])],
});

const Tooltip = ({
  placement = 'top',
  enterDelay = 100,
  enterNextDelay = 50,
  slotProps,
  ...rest
}) => (
  <MuiTooltip
    placement={placement}
    enterDelay={enterDelay}
    enterNextDelay={enterNextDelay}
    slotProps={{
      ...slotProps,
      tooltip: withCardSx(slotProps?.tooltip, HINT_CARD_SX),
      arrow: withCardSx(slotProps?.arrow, ARROW_SX),
    }}
    {...rest}
  />
);

export default Tooltip;
